import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaInTextAuthors,
  type PartialDate,
  type PersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  apaPageLocator,
  arabicVolume,
  hyphenRange,
  isWholeNumber,
  readDate,
} from "@/lib/tool-kit/citation-sources-books/common";
import {
  shortNoteTitle,
  titleCase,
} from "@/lib/tool-kit/citation-sources-more/common";
import { editionLabel } from "@/lib/tool-kit/citation-sources-more/lab-manual-citation-generator";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaYearRange,
  beforeSubtitle,
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import {
  chicagoPageRange,
  publisherName,
  splitPages,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A multivolume work — the whole set, or one volume of it — in APA 7, MLA 9 and
 * Chicago 18.
 *
 * Sources checked (October 2026). The publishers' own pages for these rules are not
 * readable by automated tools, so each rule was read through library guides that
 * reproduce it and is marked below where the guides themselves differ:
 * - APA 7: "Vol." for one volume and "Vols." for several, in parentheses after the
 *   title and after any edition (Taft College, IRSC and Eckerd guides): "Levinson, D.,
 *   & Ember, M. (Eds.). (1996). Encyclopedia of cultural anthropology (Vols. 1–4).
 *   Henry Holt." A set published over several years takes a year range, "Bowlby, J.
 *   (1976–1983). Attachment and loss (Vols. 1–3)", cited in text as (Bowlby,
 *   1976–1983). A volume with its own title puts the number and title inside the
 *   italic title, the form of APA Style's own APA Handbook example: "Travis, C. B., &
 *   White, J. W. (Eds.). (2018). APA handbook of the psychology of women: Vol. 1.
 *   History, theory, and battlegrounds. American Psychological Association." A
 *   locator for a volume inside an in-text citation ("Vol. 2, p. 321") extends the
 *   rule for citing a specific part of a source (Publication Manual, section 8.13).
 * - MLA 9: the volume number follows the title (and any edition) as an element, lower
 *   case after a comma: "Lawrence, D. H. The Letters of D. H. Lawrence. Edited by
 *   James T. Boulton, vol. 8, Cambridge UP, 2000." (University of Lethbridge guide);
 *   a whole set ends with the number of volumes, "…Pearson Education, 2009, 6 vols."
 *   and "If the volumes were published over a period of years, give the inclusive
 *   dates" (Shepherd University guide). In text, volume and page are divided by a
 *   colon, (Lawrence 8: 23-25). A volume with its own title may be cited as a stand-
 *   alone book; "Vol. 3 of …" at the end is the MLA series element, which this page
 *   adds so the reader can find the set.
 * - Chicago 18 (sections 14.18-14.24; place of publication dropped for books after
 *   1900, section 14.30), read through University of Lethbridge's CMOS 18 guide and
 *   Thompson Rivers University's guide (17th edition forms): note "Jonathan Sumption,
 *   The Hundred Years War, 4 vols. (University of Pennsylvania Press, 1990–2015)";
 *   bibliography "Gay, Peter. The Science of Freedom. Vol. 2 of The Enlightenment: An
 *   Interpretation. Knopf, 1969."; note "Peter Gay, The Enlightenment: An
 *   Interpretation, vol. 2, The Science of Freedom (Knopf, 1969), 345–49"; a volume
 *   and page in one locator, "1:388". Which title a short note shortens when the
 *   volume has its own is a convention here, not a published example.
 */

export const MULTIVOLUME_FIELDS = {
  authors: "authors",
  editors: "editors",
  title: "title",
  edition: "edition",
  volumes: "volumes",
  volumesUsed: "volumesUsed",
  volume: "volume",
  volumeTitle: "volumeTitle",
  year: "year",
  yearEnd: "yearEnd",
  publisher: "publisher",
  page: "page",
  shortTitle: "shortTitle",
} as const;

const F = MULTIVOLUME_FIELDS;

/** The whole set, or a single volume of it. */
export type MultivolumeKind = "set" | "volume";

export interface MultivolumeLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Work {
  kind: MultivolumeKind;
  names: PersonName[];
  editors: boolean;
  title: string;
  edition: string;
  volumes: string;
  volumesUsed: string;
  volume: string;
  volumeTitle: string;
  start: PartialDate | null;
  end: PartialDate | null;
  publisher: string;
  page: string;
  shortTitle: string;
}

function readWork(kind: MultivolumeKind, values: MediaValues): Work {
  const set = kind === "set";
  return {
    kind,
    names: parsePeople(get(values, F.authors)),
    editors: get(values, F.editors) === "true",
    title: capitalizeSubtitle(get(values, F.title)),
    edition: editionLabel(get(values, F.edition)),
    volumes: set ? get(values, F.volumes) : "",
    volumesUsed: set ? get(values, F.volumesUsed) : "",
    volume: arabicVolume(get(values, F.volume)),
    volumeTitle: set ? "" : capitalizeSubtitle(get(values, F.volumeTitle)),
    start: readDate(get(values, F.year)),
    end: set ? readDate(get(values, F.yearEnd)) : null,
    publisher: get(values, F.publisher),
    page: get(values, F.page),
    shortTitle: get(values, F.shortTitle),
  };
}

const RE_RANGE = /^(\d+)\s*[-–—]\s*(\d+)$/;

/** The number of volumes in the whole set, when it was typed as a number. */
function totalVolumes(work: Work): number | null {
  return isWholeNumber(work.volumes) ? Number(work.volumes) : null;
}

/** "Vol. 2", "Vols. 1–3": APA's volume label for the set or the volumes used. */
function apaVolumeLabel(work: Work): string {
  if (work.kind === "volume") {
    return work.volume && !work.volumeTitle ? `Vol. ${work.volume}` : "";
  }
  const used = work.volumesUsed.trim();
  if (used) {
    const range = RE_RANGE.exec(used);
    return range ? `Vols. ${range[1]}–${range[2]}` : `Vol. ${used}`;
  }
  const total = totalVolumes(work);
  if (total === null) {
    return work.volumes ? `Vols. ${work.volumes}` : "";
  }
  return total === 1 ? "Vol. 1" : `Vols. 1–${total}`;
}

/** "3 vols." — MLA and Chicago give the total number of volumes in the set. */
function volumeCount(work: Work): string {
  if (work.kind === "volume" || work.volumes === "") {
    return "";
  }
  return `${work.volumes} ${work.volumes === "1" ? "vol." : "vols."}`;
}

function roleSuffix(work: Work, form: "apa" | "mla" | "chicago"): string {
  const many = work.names.length > 1;
  if (form === "apa") {
    return many ? "Eds." : "Ed.";
  }
  if (form === "mla") {
    return many ? "editors" : "editor";
  }
  return many ? "eds." : "ed.";
}

/** The years a set or volume appeared, each style's own way. */
function setYears(work: Work, style: "apa" | "mla" | "chicago"): string {
  const { start, end } = work;
  if (!start) {
    return "n.d.";
  }
  if (style === "apa") {
    return apaYearRange(start, end);
  }
  if (!end || end.year === start.year) {
    return String(start.year);
  }
  if (style === "mla") {
    return `${start.year}-${end.year}`;
  }
  return chicagoPageRange(String(start.year), String(end.year));
}

/* ---------------------------------------------------------------------- APA */

function apaLead(work: Work): string {
  const names = apaAuthors(work.names);
  if (names === "") {
    return "";
  }
  if (work.editors) {
    return `${names} (${roleSuffix(work, "apa")}).`;
  }
  return `${names}${closer(names)}`;
}

function apaTitleText(work: Work): string {
  if (work.kind === "volume" && work.volumeTitle) {
    const number = work.volume ? `Vol. ${work.volume}. ` : "";
    return `${work.title}: ${number}${work.volumeTitle}`;
  }
  return work.title;
}

function apaReference(work: Work): CitationParts {
  const year = setYears(work, "apa");
  const inner = [work.edition, apaVolumeLabel(work)].filter(Boolean).join(", ");
  const paren = inner ? ` (${inner})` : "";
  const publisher = work.publisher
    ? ` ${withPeriod(publisherName(work.publisher, "apa"))}`
    : "";
  const lead = apaLead(work);
  const title = apaTitleText(work);
  if (lead === "") {
    return tidyParts([
      italic(title),
      plain(`${paren}. (${year}).${publisher}`),
    ]);
  }
  return tidyParts([
    plain(`${lead} (${year}). `),
    italic(title),
    plain(`${paren}.${publisher}`),
  ]);
}

function apaLocator(work: Work): string {
  const volume =
    work.kind === "set" && work.volume ? `Vol. ${work.volume}` : "";
  return [volume, apaPageLocator(work.page)].filter(Boolean).join(", ");
}

function apaOutput(work: Work, labels: MultivolumeLabels): CitationOutput {
  const year = setYears(work, "apa");
  const locator = apaLocator(work);
  const tail = `${year}${locator ? `, ${locator}` : ""}`;
  const short = beforeSubtitle(work.title);
  const has = work.names.length > 0;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(work) },
      {
        label: labels.parenthetical,
        parts: has
          ? [plain(`(${apaInTextAuthors(work.names)}, ${tail})`)]
          : [plain("("), italic(short), plain(`, ${tail})`)],
      },
      {
        label: labels.narrative,
        parts: has
          ? [plain(`${apaInTextAuthors(work.names, true)} (${tail})`)]
          : [italic(short), plain(` (${tail})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaLead(work: Work): string {
  const names = mlaAuthors(work.names);
  if (names === "") {
    return "";
  }
  const role = work.editors ? `${names}, ${roleSuffix(work, "mla")}` : names;
  return `${role}${closer(role)} `;
}

/** The elements after the title, comma-separated; "vol." opens a sentence as "Vol." */
function mlaElements(elements: string[]): string {
  const joined = elements.filter(Boolean).join(", ");
  return joined.startsWith("vol.") ? `V${joined.slice(1)}` : joined;
}

function mlaWorksCited(work: Work): CitationParts {
  const title = titleCase(work.title);
  const publisher = work.publisher ? publisherName(work.publisher, "mla") : "";
  const lead = mlaLead(work);
  if (work.kind === "set") {
    const tail = mlaElements([
      work.edition,
      publisher,
      work.start ? setYears(work, "mla") : "",
      volumeCount(work),
    ]);
    return tidyParts([
      plain(lead),
      italic(title),
      plain(`${closer(title)}${tail ? ` ${withPeriod(tail)}` : ""}`),
    ]);
  }
  const year = work.start ? String(work.start.year) : "";
  if (work.volumeTitle) {
    const volumeTitle = titleCase(work.volumeTitle);
    const tail = mlaElements([work.edition, publisher, year]);
    const part = work.volume ? `Vol. ${work.volume} of ` : "";
    return tidyParts([
      plain(lead),
      italic(volumeTitle),
      plain(
        `${closer(volumeTitle)}${tail ? ` ${withPeriod(tail)}` : ""} ${part}`
      ),
      italic(title),
      plain(closer(title)),
    ]);
  }
  const tail = mlaElements([
    work.edition,
    work.volume ? `vol. ${work.volume}` : "",
    publisher,
    year,
  ]);
  return tidyParts([
    plain(lead),
    italic(title),
    plain(`${closer(title)}${tail ? ` ${withPeriod(tail)}` : ""}`),
  ]);
}

function mlaInText(
  work: Work,
  labels: MultivolumeLabels
): CitationOutput["entries"][number] {
  const page = work.page ? hyphenRange(work.page) : "";
  let locator = page;
  if (work.kind === "set" && work.volume) {
    locator = page ? `${work.volume}: ${page}` : `, vol. ${work.volume}`;
  }
  const spaced = locator.startsWith(",") ? locator : ` ${locator}`;
  const tail = locator ? spaced : "";
  if (work.names.length > 0) {
    return {
      label: labels.inText,
      parts: [plain(`(${mlaInTextAuthors(work.names)}${tail})`)],
    };
  }
  const short = titleCase(beforeSubtitle(work.title));
  return {
    label: labels.inText,
    parts: [plain("("), italic(short), plain(`${tail})`)],
  };
}

function mlaOutput(work: Work, labels: MultivolumeLabels): CitationOutput {
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(work) },
      mlaInText(work, labels),
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoPages(text: string): string {
  const [from, to] = splitPages(text);
  return to ? chicagoPageRange(from, to) : from;
}

function chicagoImprint(work: Work): string {
  const publisher = work.publisher
    ? publisherName(work.publisher, "chicago")
    : "";
  return [publisher, setYears(work, "chicago")].filter(Boolean).join(", ");
}

/** The locator on a full note: "2:321" for a volume of a set, else the page. */
function chicagoNoteLocator(work: Work): string {
  const page = work.page ? chicagoPages(work.page) : "";
  if (work.kind === "set" && work.volume) {
    return page ? `${work.volume}:${page}` : "";
  }
  return page;
}

function chicagoNote(work: Work, title: string): CitationParts {
  const names = chicagoNoteAuthors(work.names);
  const role = work.editors ? `, ${roleSuffix(work, "chicago")}` : "";
  const lead = names ? `${names}${role}, ` : "";
  const locator = chicagoNoteLocator(work);
  const end = `${locator ? `, ${locator}` : ""}.`;
  const imprint = ` (${chicagoImprint(work)})`;
  if (work.kind === "set") {
    const after = [work.edition, volumeCount(work)].filter(Boolean).join(", ");
    return tidyParts([
      plain(lead),
      italic(title),
      plain(`${after ? `, ${after}` : ""}${imprint}${end}`),
    ]);
  }
  const edition = work.edition ? `, ${work.edition}` : "";
  const volume = work.volume ? `, vol. ${work.volume}` : "";
  if (work.volumeTitle) {
    return tidyParts([
      plain(lead),
      italic(title),
      plain(`${edition}${volume}, `),
      italic(titleCase(work.volumeTitle)),
      plain(`${imprint}${end}`),
    ]);
  }
  return tidyParts([
    plain(lead),
    italic(title),
    plain(`${edition}${volume}${imprint}${end}`),
  ]);
}

function chicagoShortLocator(work: Work): string {
  const page = work.page ? chicagoPages(work.page) : "";
  if (work.volume && !work.volumeTitle) {
    return page ? `${work.volume}:${page}` : `vol. ${work.volume}`;
  }
  return page;
}

function chicagoShortNote(work: Work, title: string): CitationParts {
  const subject = work.volumeTitle ? titleCase(work.volumeTitle) : title;
  const short = work.shortTitle || shortNoteTitle(subject);
  const lead =
    work.names.length > 0 ? `${chicagoShortAuthors(work.names)}, ` : "";
  const locator = chicagoShortLocator(work);
  return tidyParts([
    plain(lead),
    italic(short),
    plain(`${locator ? `, ${locator}` : ""}.`),
  ]);
}

function chicagoBibliography(work: Work, title: string): CitationParts {
  const names = chicagoBibliographyAuthors(work.names);
  const role = work.editors
    ? `${names}, ${roleSuffix(work, "chicago")}`
    : names;
  const lead = names ? `${role}${closer(role)} ` : "";
  const imprint = chicagoImprint(work);
  if (work.kind === "set") {
    const after = [work.edition, volumeCount(work)].filter(Boolean).join(" ");
    return tidyParts([
      plain(lead),
      italic(title),
      plain(`${closer(title)}${after ? ` ${after}` : ""} ${imprint}.`),
    ]);
  }
  const edition = work.edition ? ` ${work.edition}` : "";
  const volume = work.volume ? ` Vol. ${work.volume}` : "";
  if (work.volumeTitle) {
    const volumeTitle = titleCase(work.volumeTitle);
    return tidyParts([
      plain(lead),
      italic(volumeTitle),
      plain(`${closer(volumeTitle)}${edition}${volume} of `),
      italic(title),
      plain(`${closer(title)} ${imprint}.`),
    ]);
  }
  return tidyParts([
    plain(lead),
    italic(title),
    plain(
      `${closer(title)}${edition}${volume ? `${volume}.` : ""} ${imprint}.`
    ),
  ]);
}

function chicagoOutput(work: Work, labels: MultivolumeLabels): CitationOutput {
  const title = titleCase(work.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(work, title) },
      { label: labels.shortNote, parts: chicagoShortNote(work, title) },
      { label: labels.bibliography, parts: chicagoBibliography(work, title) },
    ],
    note: labels.chicagoNote,
  };
}

export function multivolumeIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatMultivolume(
  kind: MultivolumeKind,
  values: MediaValues,
  labels: MultivolumeLabels
): CitationOutput[] {
  const work = readWork(kind, values);
  return [
    apaOutput(work, labels),
    mlaOutput(work, labels),
    chicagoOutput(work, labels),
  ];
}
